/**
 * React Query hooks for Sandbox Group operations.
 *
 * @module hooks/useSandboxGroups
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sandboxGroupService } from '@/services';
import type { SandboxGroupRequest, SandboxGroupResponse } from '@/types';
import toast from 'react-hot-toast';
import { showErrorToast } from '@/utils/errorToast';

// Query key factory for sandbox groups
export const sandboxGroupKeys = {
  all: ['sandboxGroups'] as const,
  lists: () => [...sandboxGroupKeys.all, 'list'] as const,
  list: (page: number, pageSize: number) =>
    [...sandboxGroupKeys.lists(), { page, pageSize }] as const,
  details: () => [...sandboxGroupKeys.all, 'detail'] as const,
  detail: (id: string) => [...sandboxGroupKeys.details(), id] as const,
  sandboxes: (groupId: string) => [...sandboxGroupKeys.detail(groupId), 'sandboxes'] as const,
  diskImages: (groupId: string) => [...sandboxGroupKeys.detail(groupId), 'diskImages'] as const,
  snapshots: (groupId: string) => [...sandboxGroupKeys.detail(groupId), 'snapshots'] as const,
  secrets: (groupId: string) => [...sandboxGroupKeys.detail(groupId), 'secrets'] as const,
  volumes: (groupId: string) => [...sandboxGroupKeys.detail(groupId), 'volumes'] as const,
  contentPackages: (groupId: string) =>
    [...sandboxGroupKeys.detail(groupId), 'contentPackages'] as const,
};

/**
 * Hook to fetch a paginated list of sandbox groups
 */
export function useSandboxGroups(page: number = 1, pageSize: number = 25) {
  return useQuery({
    queryKey: sandboxGroupKeys.list(page, pageSize),
    queryFn: () => sandboxGroupService.list(page, pageSize),
  });
}

/**
 * Hook to fetch a single sandbox group by ID.
 * The ID can be optional to support conditional fetching (e.g., when group is not yet selected).
 */
export function useSandboxGroup(id?: string) {
  return useQuery({
    queryKey: sandboxGroupKeys.detail(id || ''),
    queryFn: () => sandboxGroupService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch sandboxes in a sandbox group
 */
export function useSandboxGroupSandboxes(groupId: string, page: number = 1, pageSize: number = 25) {
  return useQuery({
    queryKey: [...sandboxGroupKeys.sandboxes(groupId), { page, pageSize }],
    queryFn: () => sandboxGroupService.listSandboxes(groupId, page, pageSize),
    enabled: !!groupId,
    staleTime: 15 * 1000, // 15s — refresh on navigate-back
  });
}

/**
 * Hook to fetch disk images scoped to a sandbox group
 */
export function useSandboxGroupDiskImages(groupId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: sandboxGroupKeys.diskImages(groupId),
    queryFn: () => sandboxGroupService.listDiskImages(groupId),
    enabled: !!groupId && enabled,
    staleTime: 15 * 1000,
  });
}

/**
 * Hook to fetch snapshots scoped to a sandbox group (lazy — only when enabled)
 */
export function useSandboxGroupSnapshots(groupId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: sandboxGroupKeys.snapshots(groupId),
    queryFn: () => sandboxGroupService.listSnapshots(groupId),
    enabled: !!groupId && enabled,
    staleTime: 15 * 1000,
  });
}

/**
 * Hook to fetch snapshot count scoped to a sandbox group (lightweight)
 */
export function useSandboxGroupSnapshotsCount(groupId: string) {
  return useQuery({
    queryKey: [...sandboxGroupKeys.snapshots(groupId), 'count'],
    queryFn: () => sandboxGroupService.getSnapshotsCount(groupId),
    enabled: !!groupId,
    staleTime: 15 * 1000,
  });
}

/**
 * Hook to create a new sandbox group
 */
export function useCreateSandboxGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SandboxGroupRequest) => sandboxGroupService.create(request),
    onSuccess: (data: SandboxGroupResponse) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.lists() });
      toast.success(`Sandbox group ${data.id} created`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create sandbox group: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a sandbox group
 */
export function useDeleteSandboxGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sandboxGroupService.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.lists() });
      queryClient.removeQueries({ queryKey: sandboxGroupKeys.detail(id) });
      toast.success('Sandbox group deleted');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to delete sandbox group: ${error.message}`);
    },
  });
}

/**
 * Hook to update a sandbox group
 */
export function useUpdateSandboxGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: SandboxGroupRequest }) =>
      sandboxGroupService.update(id, request),
    onSuccess: (data: SandboxGroupResponse) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.detail(data.id) });
      toast.success('Sandbox group updated');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to update sandbox group: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a disk image scoped to a sandbox group
 */
export function useDeleteSandboxGroupDiskImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, diskImageId }: { groupId: string; diskImageId: string }) =>
      sandboxGroupService.deleteDiskImage(groupId, diskImageId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.diskImages(groupId) });
      toast.success('Disk image deleted');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to delete disk image: ${error.message}`);
    },
  });
}

/**
 * Hook to fetch secrets scoped to a sandbox group
 */
export function useSandboxGroupSecrets(groupId: string, enabled = true) {
  return useQuery({
    queryKey: sandboxGroupKeys.secrets(groupId),
    queryFn: () => sandboxGroupService.listSecrets(groupId),
    enabled: enabled && !!groupId,
    staleTime: 15 * 1000,
  });
}

/**
 * Hook to upsert a secret scoped to a sandbox group
 */
export function useUpsertSandboxGroupSecret(groupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ secretId, values }: { secretId: string; values: Record<string, string> }) =>
      sandboxGroupService.upsertSecret(groupId, secretId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.secrets(groupId) });
    },
  });
}

/**
 * Hook to delete a secret scoped to a sandbox group
 */
export function useDeleteSandboxGroupSecret(groupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secretId: string) => sandboxGroupService.deleteSecret(groupId, secretId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.secrets(groupId) });
    },
  });
}

/**
 * Hook to delete a snapshot scoped to a sandbox group
 */
export function useDeleteSandboxGroupSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, snapshotId }: { groupId: string; snapshotId: string }) =>
      sandboxGroupService.deleteSnapshot(groupId, snapshotId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.snapshots(groupId) });
      toast.success('Snapshot deleted');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to delete snapshot: ${error.message}`);
    },
  });
}

/**
 * Hook to fetch volumes scoped to a sandbox group
 */
export function useSandboxGroupVolumes(groupId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: sandboxGroupKeys.volumes(groupId),
    queryFn: () => sandboxGroupService.listVolumes(groupId),
    enabled: !!groupId && enabled,
    staleTime: 0, // Always refetch on mount so attachment state is current
  });
}

/**
 * Hook to fetch a single volume by name scoped to a sandbox group
 */
export function useSandboxGroupVolume(groupId: string, volumeName: string) {
  return useQuery({
    queryKey: [...sandboxGroupKeys.volumes(groupId), volumeName],
    queryFn: () => sandboxGroupService.getVolume(groupId, volumeName),
    enabled: !!groupId && !!volumeName,
    staleTime: 0, // Always refetch on mount so attachment state is current
  });
}

/**
 * Hook to create a volume scoped to a sandbox group
 */
export function useCreateSandboxGroupVolume() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      name,
      type,
      size,
      labels,
    }: {
      groupId: string;
      name: string;
      type: string;
      size?: string;
      labels?: Record<string, string>;
    }) => sandboxGroupService.createVolume(groupId, name, { type, size, labels }),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.volumes(groupId) });
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create volume: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a volume scoped to a sandbox group
 */
export function useDeleteSandboxGroupVolume() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, volumeName }: { groupId: string; volumeName: string }) =>
      sandboxGroupService.deleteVolume(groupId, volumeName),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.volumes(groupId) });
      toast.success('Volume deleted');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to delete volume: ${error.message}`);
    },
  });
}

export function useForkSandboxGroupVolume() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      sourceVolumeName,
      destinationVolumeName,
      labels,
    }: {
      groupId: string;
      sourceVolumeName: string;
      destinationVolumeName: string;
      labels?: Record<string, string>;
    }) =>
      sandboxGroupService.forkVolume(groupId, sourceVolumeName, { destinationVolumeName, labels }),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.volumes(groupId) });
    },
  });
}
